/**
 * LE MODÈLE DU STUDIO DES ARTICLES RAPIDES — les gestes sur l'arbre, sans rendu.
 *
 * Chaque geste rend un NOUVEAU document : l'écran empile les précédents pour annuler (maquette : dix pas, Ctrl+Z),
 * et c'est ce document entier qui part au brouillon du serveur. Les règles de forme sont celles que le serveur
 * refuse (`catalog/quick-studio.ts`) — un parent est un dossier ou une source de la même disposition, un dossier ne se
 * contient pas, une caisse n'est que dans une disposition — tenues ici pour que l'écran ne propose jamais un geste refusé.
 * ⚠️ LE NIVEAU D'UNE SOURCE (famille, sous-famille, marque) SE RANGE PAR LA RÈGLE DU CONTRAT (`sourceLevel`) : celle
 *    que la caisse applique. Ses articles déversés ne sont pas des tuiles — une cellule les nomme `@<article>`, une clé
 *    qu'aucun identifiant de tuile ne peut prendre (le serveur n'en accepte pas le `@`).
 * ⚠️ UN PRÉRÉGLAGE (« Toutes les familles »…) SE DÉPLOIE À LA LECTURE (`expandLayout`, règle du contrat) : ses tuiles
 *    sources ne sont jamais dans le document, et ce qui en revient est DÉPOUILLÉ (`stripExpanded`) avant d'être
 *    enregistré — une tuile déployée enregistrée cesserait de suivre le référentiel.
 */
import { formatMoney } from '@orgazone/money-format';
import {
  PRESET_CHILD_MARK, PRESET_SOURCE, QUICK_POUR_MAX, articleKey, boundSourceLevel, deepArticleCount, expandPresets, isPresetChildId, isPresetKind,
  isSourceKind, opensLevel, QUICK_SCREEN_DEFAULT_VISUAL, screenLook, sourceCount, sourceLevel, tileKey, type QuickPresetKind,
  type QuickSourceKind, type QuickTileLook, type QuickTileVisual,
} from '@orgazone/sync-contract';
import type { StudioArticle, StudioDocument, StudioLayout, StudioSettings, StudioSource, StudioTile, StudioTileKind } from '../api';
import { t } from '../i18n';
import { fieldLabel } from '../vocabulary';

/** Pas d'annulation gardés — ceux de la maquette. */
export const HISTORY_MAX = 10;
export const COLS_MIN = 3;
export const COLS_MAX = 10;
/** Tuiles par disposition — le plafond du serveur, dit avant qu'il ne refuse. */
export const MAX_TILES = 1000;

/** Le mot de la source, tel que le MAGASIN le dit : « Famille » peut être renommée dans son vocabulaire. */
export function sourceWord(kind: QuickSourceKind): string {
  return kind === 'family' ? fieldLabel('family') : kind === 'sub_family' ? fieldLabel('subFamily') : fieldLabel('brand');
}

/**
 * Le nom d'un préréglage — et le libellé que sa tuile reçoit à la création.
 * ⚠️ PAS « Toutes les familles » : le mot vient du vocabulaire du magasin (« Millésime ») et le vocabulaire n'a pas de
 *    pluriel ; la phrase garde donc le mot tel quel.
 */
export function presetLabel(kind: QuickPresetKind): string {
  return t('Tout le référentiel : {champ}', { champ: sourceWord(PRESET_SOURCE[kind]) });
}

/** Le nom d'une nature, tel que l'écran le dit. */
export function kindLabel(kind: StudioTileKind): string {
  if (isSourceKind(kind)) return sourceWord(kind);
  if (isPresetKind(kind)) return presetLabel(kind);
  return kind === 'folder' ? t('Dossier') : kind === 'article' ? t('Article') : t('Case vide');
}

/** Le prix d'un article tel que la caisse l'appliquera — « Prix libre » quand il se saisit en caisse. */
export function formatPrice(cents: number | null): string {
  return cents == null ? t('Prix libre') : formatMoney(cents, { thousandsSep: ' ' });
}

/** Un identifiant de tuile neuf — tiré au hasard : le même article peut vivre à plusieurs niveaux. */
export function newTileId(): string {
  return `qt-${crypto.randomUUID()}`;
}

/** La clé d'une source dans les tables du studio (`StudioView.sources`, contenu déversé). */
export const sourceRef = (kind: QuickSourceKind, id: string): string => `${kind}:${id}`;
/** Une tuile qui déverse une famille, une sous-famille ou une marque. */
export const isSourceTile = (tile: StudioTile): boolean => isSourceKind(tile.kind);
/** La source d'une tuile, si elle en est une. */
export const sourceRefOf = (tile: StudioTile): string | null =>
  isSourceKind(tile.kind) && tile.sourceId ? sourceRef(tile.kind, tile.sourceId) : null;
/**
 * La source que désigne une clé `sourceRef` — `null` pour une clé qui n'en est pas une.
 * ⚠️ Coupée au PREMIER deux-points : la nature n'en contient pas, l'identifiant d'une valeur de référentiel peut-être.
 */
export function parseSourceRef(ref: string): { kind: QuickSourceKind; id: string } | null {
  const at = ref.indexOf(':');
  const kind = ref.slice(0, Math.max(at, 0));
  const id = ref.slice(at + 1);
  return at > 0 && id && isSourceKind(kind) ? { kind, id } : null;
}

/** La cellule d'un article DÉVERSÉ par une source — jamais un identifiant de tuile. */
export const pouredKey = (articleId: string): string => `@${articleId}`;
export const isPouredKey = (key: string): boolean => key.startsWith('@');
export const pouredArticle = (key: string): string => key.slice(1);

/** Ce qu'une source déverse, tel que l'écran le connaît. */
export type PourState =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'ready'; ids: string[]; total: number };

/** Les tuiles d'un niveau, dans leur ordre (`null` = l'accueil). */
export function levelTiles(layout: StudioLayout, parentId: string | null): StudioTile[] {
  return layout.tiles.filter((tile) => tile.parentId === parentId);
}

export function tileById(layout: StudioLayout, id: string): StudioTile | undefined {
  return layout.tiles.find((tile) => tile.id === id);
}

/** Les descendants d'une tuile — ce que sa suppression emporte, et ce qu'un déplacement ne peut pas viser. */
export function descendantIds(layout: StudioLayout, id: string): Set<string> {
  const out = new Set<string>();
  const walk = (parent: string): void => {
    for (const child of layout.tiles) {
      if (child.parentId !== parent || out.has(child.id)) continue;
      out.add(child.id);
      walk(child.id);
    }
  };
  walk(id);
  return out;
}

/** Les niveaux traversés depuis l'accueil pour atteindre celui-ci (lui compris). */
export function pathTo(layout: StudioLayout, folderId: string | null): string[] {
  const path: string[] = [];
  const seen = new Set<string>();
  let current = folderId;
  while (current !== null && !seen.has(current)) {
    seen.add(current);
    path.unshift(current);
    current = tileById(layout, current)?.parentId ?? null;
  }
  return path;
}

/**
 * LE NOM D'UNE TUILE : le libellé posé, sinon celui de l'article ou de la source, sinon sa nature.
 * ⚠️ Un article que le catalogue ne connaît plus garde un nom lisible plutôt qu'un identifiant — la publication le
 *    refusera, et l'écran doit pouvoir dire lequel.
 */
export function tileName(tile: StudioTile, articles: Record<string, StudioArticle>, sources: Record<string, StudioSource>): string {
  if (tile.label) return tile.label;
  if (tile.kind === 'article') return articles[tile.articleId ?? '']?.name ?? t('Article introuvable');
  const ref = sourceRefOf(tile);
  if (ref) return sources[ref]?.label ?? kindLabel(tile.kind);
  return kindLabel(tile.kind);
}

/** Remplace une disposition du document — les autres restent les mêmes objets. */
export function withLayout(doc: StudioDocument, layoutId: string, change: (l: StudioLayout) => StudioLayout): StudioDocument {
  return { ...doc, layouts: doc.layouts.map((l) => (l.id === layoutId ? change(l) : l)) };
}

/** L'habillage d'une tuile — teinte, visuel, picto, photo et sa taille. */
export type TileLook = Pick<StudioTile, 'color' | 'visual' | 'icon' | 'photo' | 'photoSize'>;
/** Aucun habillage des articles d'une source : chacun prend le sien, sinon celui de l'écran (`screenLook`). */
export const NO_POUR: QuickTileLook = { color: null, visual: null, icon: null, photoSize: null };
/** Une tuile sans habillage : le rendu par défaut de la caisse. */
export const NO_LOOK: TileLook & Pick<StudioTile, 'pour'> = { color: null, visual: null, icon: null, photo: null, photoSize: null, pour: NO_POUR };
/** Ce qu'un geste de l'inspecteur peut changer sur une tuile. */
export type TilePatch = Partial<Pick<StudioTile, 'label' | 'w' | 'h' | 'pour'> & TileLook>;

/** Une tuile neuve — sans source, retouche ni habillage tant que sa nature n'en demande pas. */
type NewTile = Pick<StudioTile, 'kind' | 'articleId' | 'label' | 'w' | 'h'> & Partial<Pick<StudioTile, 'sourceId'>>;

// ── Le visuel des nouvelles tuiles et sa propagation (maquette : `defVis` / `visStamp` / `dvCount` / `dvApply`) ──

/** LE VISUEL DE L'ÉCRAN — celui que « Appliquer à toutes les tuiles… » a posé, sinon « Petit picto » (le contrat). */
export function screenVisual(settings: StudioSettings): QuickTileVisual {
  return settings.visual ?? QUICK_SCREEN_DEFAULT_VISUAL;
}

/** LE VISUEL QUE PRENNENT LES TUILES QU'ON AJOUTE — le choix de la disposition, sinon celui de l'écran. */
export function newTilesVisual(settings: StudioSettings): QuickTileVisual {
  return settings.newVisual ?? screenVisual(settings);
}

/**
 * CE QU'UNE TUILE PORTE DÈS SA CRÉATION — le visuel des nouvelles tuiles, posé sur elle quand il diffère de ce qu'elle
 * prendrait déjà (celui des articles de l'écran pour un article, celui de l'écran sinon) ; une source ou un préréglage
 * le donne aussi à ce qu'il génère (« Ses articles ») quand il diffère de celui de l'écran.
 * ⚠️ CHOISIR LE VISUEL DES NOUVELLES TUILES NE TOUCHE AUCUNE TUILE POSÉE : il ne vaut qu'à la naissance d'une tuile.
 * ⚠️ RIEN POUR UNE CASE VIDE : elle ne s'habille pas.
 */
export function newTileStamp(settings: StudioSettings, kind: StudioTileKind): { visual?: QuickTileVisual; pour?: QuickTileLook } {
  if (kind === 'empty') return {};
  const nv = newTilesVisual(settings);
  const base = screenVisual(settings);
  const out: { visual?: QuickTileVisual; pour?: QuickTileLook } = {};
  if (nv !== (kind === 'article' ? settings.articlesVisual ?? base : base)) out.visual = nv;
  if ((isSourceKind(kind) || isPresetKind(kind)) && nv !== base) out.pour = { ...NO_POUR, visual: nv };
  return out;
}

/** Ce que changerait la propagation du visuel des nouvelles tuiles : toutes les tuiles, ou les articles seuls, et combien ont un visuel à elles. */
export interface ScreenVisualCount { all: number; allOwn: number; art: number; artOwn: number }

/** Les articles qu'une source déverse dans son niveau, borne de la caisse comprise — `null` tant que ce n'est pas lu. */
function pouredCount(
  layout: StudioLayout, source: StudioTile, pours: Record<string, PourState>, sources: Record<string, StudioSource>,
): number | null {
  const ref = sourceRefOf(source);
  if (!ref) return 0;
  // Une tuile déployée par un préréglage n'a ni tuile posée ni retrait : le compte du serveur est le sien.
  if (isPresetChildId(source.id)) return sources[ref]?.count ?? null;
  const pour = pours[ref];
  if (pour?.status !== 'ready') return null;
  const level = boundSourceLevel(sourceLevel(levelTiles(layout, source.id), pour.ids, source.hidden, source.order), QUICK_POUR_MAX);
  return level.entries.filter((e) => e.tile === null).length;
}

/**
 * LES TUILES QUI NE SONT PAS DANS LE VISUEL CHOISI — ce que la propagation changerait, telles que la caisse les dessine
 * (`screenLook`) : les tuiles posées, celles qu'un préréglage déploie et les articles que déverse une source. Une tuile
 * « à elle » porte son propre visuel (réglé une à une, ou posé à sa création).
 * ⚠️ LES BOUTONS DU MENU FIXE NE SONT PAS COMPTÉS (maquette `dvCount`) : on les règle un à un, au rail ; leur contenu, si.
 * ⚠️ `null` TANT QU'UN CONTENU DÉVERSÉ N'EST PAS LU : un compte partiel annoncerait moins de tuiles qu'il n'en change.
 * - `layout` : la disposition DÉPLOYÉE (`expandLayout`).
 */
export function screenVisualCount(
  layout: StudioLayout, visual: QuickTileVisual, pours: Record<string, PourState>, sources: Record<string, StudioSource>,
): ScreenVisualCount | null {
  const s = layout.settings;
  const r: ScreenVisualCount = { all: 0, allOwn: 0, art: 0, artOwn: 0 };
  for (const t of layout.tiles) {
    if (t.kind === 'empty') continue;
    const parent = t.parentId === null ? undefined : tileById(layout, t.parentId);
    const fromSource = parent && isSourceKind(parent.kind) ? parent.pour : null;
    if (!(s.rail && t.parentId === null) && screenLook(t.kind, t, t.kind === 'article' ? fromSource : null, s).visual !== visual) {
      const own = t.visual !== null && !isPresetChildId(t.id);
      r.all += 1;
      if (own) r.allOwn += 1;
      if (t.kind === 'article') { r.art += 1; if (own) r.artOwn += 1; }
    }
    if (isSourceKind(t.kind) && screenLook('article', null, t.pour, s).visual !== visual) {
      const n = pouredCount(layout, t, pours, sources);
      if (n === null) return null;
      r.all += n;
      r.art += n;
    }
  }
  return r;
}

/**
 * PROPAGER LE VISUEL DES NOUVELLES TUILES — à tout l'écran (`all` : il devient le visuel de l'écran, plus aucun visuel
 * propre ni « Ses articles ») ou aux articles seuls (`articles` : le visuel des articles de l'écran, les dossiers, familles
 * et marques gardent le leur).
 * ⚠️ LES PHOTOS DÉPOSÉES SONT GARDÉES : repasser une tuile en « Photo » les retrouve.
 */
export function applyScreenVisual(layout: StudioLayout, scope: 'all' | 'articles'): StudioLayout {
  const nv = newTilesVisual(layout.settings);
  const tiles = layout.tiles.map((t) => {
    if (isPresetChildId(t.id)) return t;
    if (scope === 'articles') return t.kind === 'article' && t.visual !== null ? { ...t, visual: null } : t;
    if (t.visual === null && t.pour.visual === null) return t;
    return { ...t, visual: null, pour: { ...t.pour, visual: null } };
  });
  const settings = scope === 'articles'
    ? { ...layout.settings, articlesVisual: nv }
    : { ...layout.settings, visual: nv, articlesVisual: null };
  return { ...layout, settings, tiles };
}

/** Ajoute une tuile en fin de niveau — avec le visuel des nouvelles tuiles (`newTileStamp`). */
export function addTile(layout: StudioLayout, parentId: string | null, tile: NewTile): { layout: StudioLayout; id: string } {
  const id = newTileId();
  const full: StudioTile = { sourceId: null, ...NO_LOOK, ...newTileStamp(layout.settings, tile.kind), ...tile, id, parentId, hidden: [], order: [] };
  return { layout: { ...layout, tiles: [...layout.tiles, full] }, id };
}

/**
 * AJOUTE DES ARTICLES À UN NIVEAU — sans doublon DANS ce niveau (maquette `artAdd`) : deux tuiles identiques côte à
 * côte ne servent personne. Le même article reste possible ailleurs dans l'arbre.
 */
export function addArticles(
  layout: StudioLayout, parentId: string | null, articleIds: string[],
): { layout: StudioLayout; added: string[]; skipped: number } {
  const here = new Set(levelTiles(layout, parentId).filter((x) => x.kind === 'article').map((x) => x.articleId));
  const added: string[] = [];
  const tiles = [...layout.tiles];
  for (const articleId of articleIds) {
    if (here.has(articleId)) continue;
    here.add(articleId);
    const id = newTileId();
    tiles.push({ id, parentId, kind: 'article', articleId, label: null, w: 1, h: 1, sourceId: null, hidden: [], order: [], ...NO_LOOK, ...newTileStamp(layout.settings, 'article') });
    added.push(id);
  }
  return { layout: { ...layout, tiles }, added, skipped: articleIds.length - added.length };
}

/**
 * CE QU'UNE SOURCE GARDE DE TUILES QUI LA QUITTENT (retirées ou rangées ailleurs) : la clé de chacune sort de l'ordre
 * retouché — une clé qui ne désigne plus rien s'accumulerait jusqu'au plafond des retouches.
 * ⚠️ UN ARTICLE QUI QUITTE UNE SOURCE Y EST MASQUÉ, À SA PLACE : la source le déverserait aussitôt sinon, et « Retirer »
 *    n'aurait rien retiré. Sa clé d'article prend la place de sa clé de tuile, donc « rétablir » le rend là où il était.
 *    La maquette le masquait sans le compter (« −n ») ni le rétablir — un retrait invisible et définitif ; ici, un
 *    article retiré reste au catalogue et se rétablit depuis son niveau, comme tout article retiré d'une source.
 */
function releaseFromSource(source: StudioTile, leaving: readonly StudioTile[]): StudioTile {
  if (isPresetKind(source.kind)) return releaseFromPreset(source, leaving);
  if (!isSourceKind(source.kind)) return source;
  const swap = new Map<string, string | null>(leaving.map((x) => [
    tileKey(x.id), x.kind === 'article' && x.articleId ? articleKey(x.articleId) : null,
  ]));
  // ⚠️ Jamais deux fois la même clé : le serveur refuserait le brouillon (« figure deux fois »).
  const seen = new Set(source.order.filter((k) => !swap.has(k)));
  const order: string[] = [];
  for (const k of source.order) {
    const to = swap.has(k) ? swap.get(k) : k;
    if (!to || (to !== k && seen.has(to))) continue;
    seen.add(to);
    order.push(to);
  }
  const hide = leaving.flatMap((x) => (x.kind === 'article' && x.articleId ? [x.articleId] : []));
  return { ...source, order, hidden: hide.length > 0 ? [...new Set([...source.hidden, ...hide])] : source.hidden };
}

/**
 * CE QU'UN PRÉRÉGLAGE GARDE D'UNE TUILE SOURCE DE SON RÉFÉRENTIEL QUI LE QUITTE : sa valeur y est RETIRÉE.
 * ⚠️ Posée là, elle remplaçait la tuile déployée de la même valeur (`expandPresets`) ; partie sans être masquée, la
 *    valeur reviendrait aussitôt par le déploiement, et « Retirer » n'aurait rien retiré (même règle qu'un article
 *    retouché qui quitte sa source).
 */
function releaseFromPreset(preset: StudioTile, leaving: readonly StudioTile[]): StudioTile {
  const kind = PRESET_SOURCE[preset.kind as QuickPresetKind];
  const hide = leaving.flatMap((x) => (x.kind === kind && x.sourceId ? [x.sourceId] : []));
  return hide.length > 0 ? { ...preset, hidden: [...new Set([...preset.hidden, ...hide])] } : preset;
}

// ── Les PRÉRÉGLAGES ─────────────────────────────────────────────────────────────────────────────

/** Les valeurs qu'un préréglage déploie, telles que l'écran les a lues (`GET /quick-studio/sources`). */
export type PresetValuesState =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'ready'; rows: StudioSource[] };

/** Les référentiels qu'une disposition déploie — ceux de ses préréglages. */
export function presetKindsOf(layout: StudioLayout): QuickSourceKind[] {
  return [...new Set(layout.tiles.filter((x) => isPresetKind(x.kind)).map((x) => PRESET_SOURCE[x.kind as QuickPresetKind]))];
}

/**
 * LA DISPOSITION TELLE QU'ON LA VOIT — ses préréglages déployés, une tuile source par valeur, habillée comme lui.
 * ⚠️ Un référentiel pas encore lu ne déploie rien : le préréglage le dit (`presetState`), il ne montre pas un niveau vide
 *    comme s'il l'était.
 */
export function expandLayout(layout: StudioLayout, values: Partial<Record<QuickSourceKind, PresetValuesState>>): StudioLayout {
  if (!layout.tiles.some((x) => isPresetKind(x.kind))) return layout;
  const tiles = expandPresets(
    layout.tiles,
    (kind) => { const v = values[kind]; return v?.status === 'ready' ? v.rows : []; },
    (preset, kind, valueId, childId): StudioTile => ({
      id: childId, parentId: preset.id, kind, articleId: null, label: null, w: 1, h: 1, sourceId: valueId, hidden: [], order: [],
      color: preset.color, visual: preset.visual, icon: preset.icon, photo: preset.photo, photoSize: preset.photoSize,
      // ⚠️ L'HABILLAGE DES ARTICLES DU PRÉRÉGLAGE descend à chacune de ses sources : leurs articles le prennent.
      pour: preset.pour,
    }),
  );
  return { ...layout, tiles };
}

/**
 * CE QUI REVIENT D'UN GESTE FAIT SUR LA DISPOSITION DÉPLOYÉE, PRÊT À ENREGISTRER — sans ses tuiles déployées.
 * ⚠️ L'INVARIANT EST ICI ET NON DANS CHAQUE GESTE : un geste qui oublierait d'écarter une tuile déployée l'enregistrerait
 *    sinon, et elle cesserait de suivre le référentiel (le serveur la refuserait de toute façon : `~` hors format).
 */
export function stripExpanded(layout: StudioLayout): StudioLayout {
  return layout.tiles.some((x) => isPresetChildId(x.id)) ? { ...layout, tiles: layout.tiles.filter((x) => !isPresetChildId(x.id)) } : layout;
}

/** Le préréglage et la valeur d'une tuile déployée — `null` pour toute autre tuile. */
export function presetChildOf(id: string): { presetId: string; valueId: string } | null {
  const at = id.indexOf(PRESET_CHILD_MARK);
  return at > 0 ? { presetId: id.slice(0, at), valueId: id.slice(at + PRESET_CHILD_MARK.length) } : null;
}

/** RETIRE DES VALEURS D'UN PRÉRÉGLAGE — elles restent au référentiel, et le préréglage les rétablit d'un geste. */
export function hidePresetValues(layout: StudioLayout, presetId: string, valueIds: string[]): StudioLayout {
  return { ...layout, tiles: layout.tiles.map((x) => (x.id === presetId ? { ...x, hidden: [...new Set([...x.hidden, ...valueIds])] } : x)) };
}

/**
 * CE QU'ANNONCE UNE TUILE SOURCE, déployée ou posée.
 * ⚠️ UNE TUILE DÉPLOYÉE ANNONCE LE COMPTE QUE LE SERVEUR A MESURÉ POUR SA VALEUR (\`StudioSource.count\` : ses déclinaisons
 *    vendables) — elle n'a ni tuile posée ni retrait, c'est donc le compte exact, et le lire évite une lecture de contenu
 *    par valeur (un préréglage de marques en déploie des centaines). Une tuile posée se compte sur son contenu.
 */
export function sourceTileTotal(
  layout: StudioLayout, tile: StudioTile, pours: Record<string, PourState>, articles: Record<string, StudioArticle>,
  sources: Record<string, StudioSource>,
): number | null {
  if (isPresetChildId(tile.id)) { const ref = sourceRefOf(tile); return ref ? sources[ref]?.count ?? null : null; }
  return sourceTileCount(layout, tile, pours, articles);
}

/**
 * LES ARTICLES QU'OUVRE UNE TUILE, TOUS NIVEAUX CONFONDUS — sa pastille (`deepArticleCount`, au contrat, la règle de la
 * caisse). `null` tant qu'une source de son arbre n'est pas lue, ou qu'un préréglage n'a pas reçu son référentiel.
 * - `layout` : la disposition DÉPLOYÉE (`expandLayout`), où un préréglage porte ses valeurs.
 */
export function deepTileTotal(
  layout: StudioLayout, tile: StudioTile, pours: Record<string, PourState>, articles: Record<string, StudioArticle>,
  sources: Record<string, StudioSource>, presetValues: Partial<Record<QuickSourceKind, PresetValuesState>>,
): number | null {
  return deepArticleCount(
    tile,
    (x) => (isPresetKind(x.kind) && presetValues[PRESET_SOURCE[x.kind]]?.status !== 'ready' ? null : levelTiles(layout, x.id)),
    (x) => sourceTileTotal(layout, x, pours, articles, sources),
    (articleId) => articles[articleId]?.active === true,
  );
}


/** Les valeurs retirées d'un préréglage que son référentiel porte ENCORE — le « −N » ; \`null\` tant qu'il n'est pas lu. */
export function presetHiddenCount(tile: StudioTile, values: Partial<Record<QuickSourceKind, PresetValuesState>>): number | null {
  if (!isPresetKind(tile.kind)) return null;
  const v = values[PRESET_SOURCE[tile.kind]];
  if (v?.status !== 'ready') return null;
  const live = new Set(v.rows.map((r) => r.id));
  return tile.hidden.filter((id) => live.has(id)).length;
}

/** RÉTABLIT LES VALEURS RETIRÉES D'UN PRÉRÉGLAGE. */
export function restorePresetValues(layout: StudioLayout, presetId: string): StudioLayout {
  return { ...layout, tiles: layout.tiles.map((x) => (x.id === presetId ? { ...x, hidden: [] } : x)) };
}

/**
 * PERSONNALISER UNE VALEUR DÉPLOYÉE : une vraie tuile source de cette valeur prend sa place dans le préréglage — elle
 * se nomme, s'habille et se range comme toute tuile ; la tuile déployée cède (`expandPresets`, valeur déjà posée).
 */
export function customizePresetValue(layout: StudioLayout, childId: string): { layout: StudioLayout; id: string } | null {
  const ref = presetChildOf(childId);
  const preset = ref ? tileById(layout, ref.presetId) : undefined;
  if (!ref || !preset || !isPresetKind(preset.kind)) return null;
  return addTile(layout, preset.id, {
    kind: PRESET_SOURCE[preset.kind], articleId: null, label: null, w: 1, h: 1, sourceId: ref.valueId,
  });
}

/**
 * RETIRE DES TUILES ET LEURS DESCENDANTS — un dossier retiré n'abandonne pas ses tuiles en orphelines (la maquette
 * les laissait dans l'arbre, invisibles de partout ; le serveur les refuserait).
 */
export function removeTiles(layout: StudioLayout, ids: Iterable<string>): StudioLayout {
  const gone = new Set<string>();
  for (const id of ids) {
    gone.add(id);
    for (const d of descendantIds(layout, id)) gone.add(d);
  }
  return {
    ...layout,
    tiles: layout.tiles.filter((tile) => !gone.has(tile.id)).map((tile) => {
      const leaving = layout.tiles.filter((x) => x.parentId === tile.id && gone.has(x.id));
      return leaving.length > 0 ? releaseFromSource(tile, leaving) : tile;
    }),
  };
}

/**
 * RANGE DES TUILES DANS UN DOSSIER OU UNE SOURCE (ou à l'accueil), en fin de niveau.
 * ⚠️ Jamais dans une tuile déplacée ni dans l'un de ses descendants : un dossier rangé dans lui-même disparaîtrait
 *    de l'arbre. La cible est refusée en bloc plutôt que corrigée.
 */
export function moveTiles(layout: StudioLayout, ids: string[], toParent: string | null): StudioLayout {
  const moving = new Set(ids);
  if (toParent !== null) {
    for (const id of ids) if (id === toParent || descendantIds(layout, id).has(toParent)) return layout;
    const target = tileById(layout, toParent);
    if (!target || !opensLevel(target.kind)) return layout;
  }
  // Une source que des tuiles quittent les relâche (`releaseFromSource`) ; ranger dans le niveau où l'on est déjà ne
  // quitte rien.
  const leaving = (parentId: string): StudioTile[] =>
    parentId === toParent ? [] : layout.tiles.filter((x) => moving.has(x.id) && x.parentId === parentId);
  const kept = layout.tiles.filter((tile) => !moving.has(tile.id)).map((tile) => {
    const out = leaving(tile.id);
    return out.length > 0 ? releaseFromSource(tile, out) : tile;
  });
  const moved = layout.tiles.filter((tile) => moving.has(tile.id)).map((tile) => ({ ...tile, parentId: toParent }));
  return { ...layout, tiles: [...kept, ...moved] };
}

/** RÉORDONNE UN NIVEAU — les tuiles des autres niveaux gardent leur place relative. */
export function reorderLevel(layout: StudioLayout, parentId: string | null, orderedIds: readonly string[]): StudioLayout {
  const level = levelTiles(layout, parentId);
  const byId = new Map(level.map((tile) => [tile.id, tile]));
  // ⚠️ Un identifiant en double ou inconnu est ignoré : sinon une tuile serait dupliquée, ou une autre perdue.
  const wanted = [...new Set(orderedIds)].filter((id) => byId.has(id));
  const ordered = wanted.map((id) => byId.get(id)!);
  // Une tuile absente de l'ordre demandé (ajoutée pendant le geste) garde sa place en fin de niveau.
  for (const tile of level) if (!wanted.includes(tile.id)) ordered.push(tile);
  let i = 0;
  return { ...layout, tiles: layout.tiles.map((tile) => (tile.parentId === parentId ? ordered[i++]! : tile)) };
}

/** Déplace une tuile d'un cran dans son niveau — le geste des flèches du clavier, hors niveau de source. */
export function nudgeTile(layout: StudioLayout, id: string, delta: number): StudioLayout {
  const tile = tileById(layout, id);
  if (!tile) return layout;
  const ids = levelTiles(layout, tile.parentId).map((x) => x.id);
  const from = ids.indexOf(id);
  const to = from + delta;
  if (to < 0 || to >= ids.length) return layout;
  ids.splice(from, 1);
  ids.splice(to, 0, id);
  return reorderLevel(layout, tile.parentId, ids);
}

export function patchTile(layout: StudioLayout, id: string, patch: TilePatch): StudioLayout {
  return { ...layout, tiles: layout.tiles.map((tile) => (tile.id === id ? { ...tile, ...patch } : tile)) };
}

/**
 * CHANGE CE QU'UNE SOURCE DÉVERSE — et oublie ses retouches : les articles masqués et l'ordre désignaient ceux de
 * l'ancienne source, et garderaient en silence une trace qu'aucun écran ne montre plus.
 */
export function setSource(layout: StudioLayout, id: string, sourceId: string): StudioLayout {
  return { ...layout, tiles: layout.tiles.map((tile) => (tile.id === id ? { ...tile, sourceId, hidden: [], order: [] } : tile)) };
}

/** Les niveaux où l'on peut ranger ces tuiles — dossiers et sources, sauf elles et leurs descendants (`univTargets`). */
export function folderTargets(layout: StudioLayout, moving: Iterable<string>): { id: string | null; path: string[]; depth: number }[] {
  const bad = new Set<string>();
  for (const id of moving) {
    bad.add(id);
    for (const d of descendantIds(layout, id)) bad.add(d);
  }
  const out: { id: string | null; path: string[]; depth: number }[] = [{ id: null, path: [], depth: 0 }];
  const walk = (parent: string | null, path: string[]): void => {
    for (const tile of levelTiles(layout, parent)) {
      // ⚠️ Jamais DANS une tuile déployée : elle n'est pas au document, une tuile rangée dedans y deviendrait orpheline.
      if (!opensLevel(tile.kind) || bad.has(tile.id) || isPresetChildId(tile.id)) continue;
      const here = [...path, tile.id];
      out.push({ id: tile.id, path: here, depth: here.length });
      walk(tile.id, here);
    }
  };
  walk(null, []);
  return out;
}

// ── Le niveau d'une SOURCE ──────────────────────────────────────────────────────────────────────

/** Le niveau ouvert, en cellules : une tuile (son identifiant) ou un article déversé (`@<article>`). */
export interface LevelCells {
  keys: string[];
  /** Articles déversés au-delà de la borne de la caisse — DITS, jamais tus (A3). */
  beyond: number;
  /** Le contenu déversé n'est pas encore lu, ou ne se lit pas : les tuiles posées s'affichent, la suite le dit. */
  pour: 'none' | 'ready' | 'loading' | 'error';
  message?: string;
}

/**
 * LES CELLULES D'UN NIVEAU — les tuiles d'un dossier ; pour une source, ses tuiles posées et ce qu'elle déverse, rangés
 * et bornés par la règle du contrat, celle de la caisse.
 */
export function levelCells(layout: StudioLayout, parentId: string | null, pours: Record<string, PourState>): LevelCells {
  const own = levelTiles(layout, parentId);
  const parent = parentId === null ? undefined : tileById(layout, parentId);
  const ref = parent ? sourceRefOf(parent) : null;
  if (!parent || !ref) return { keys: own.map((x) => x.id), beyond: 0, pour: 'none' };
  const pour = pours[ref];
  if (!pour || pour.status !== 'ready') {
    return { keys: own.map((x) => x.id), beyond: 0, pour: pour?.status ?? 'loading', message: pour?.status === 'error' ? pour.message : undefined };
  }
  const bound = boundSourceLevel(sourceLevel(own, pour.ids, parent.hidden, parent.order), QUICK_POUR_MAX);
  return { keys: bound.entries.map((e) => (e.tile !== null ? e.tile.id : pouredKey(e.articleId))), beyond: bound.beyond, pour: 'ready' };
}

/**
 * CE QU'ANNONCE UNE SOURCE — le compte réel de ce que son niveau dessine (tuiles posées vendables, articles déversés
 * non masqués), borne de la caisse comprise ; `null` tant que son contenu n'est pas lu, plutôt qu'un chiffre faux.
 */
export function sourceTileCount(
  layout: StudioLayout, tile: StudioTile, pours: Record<string, PourState>, articles: Record<string, StudioArticle>,
): number | null {
  const ref = sourceRefOf(tile);
  const pour = ref ? pours[ref] : undefined;
  if (!pour || pour.status !== 'ready') return null;
  const poured = new Set(pour.ids);
  const sellable = (articleId: string): boolean => poured.has(articleId) || articles[articleId]?.active === true;
  return sourceCount(sourceLevel(levelTiles(layout, tile.id), pour.ids, tile.hidden, tile.order), sellable);
}

/**
 * LES ARTICLES DÉJÀ PRÉSENTS DANS UN NIVEAU — ses tuiles d'article, et pour une source ce qu'elle déverse sans le
 * masquer, borne comprise : la fenêtre d'ajout les marque « déjà là » plutôt que d'en faire un doublon.
 */
export function levelArticleIds(layout: StudioLayout, parentId: string | null, pours: Record<string, PourState>): Set<string> {
  const ids = new Set(levelTiles(layout, parentId).flatMap((x) => (x.kind === 'article' && x.articleId ? [x.articleId] : [])));
  const parent = parentId === null ? undefined : tileById(layout, parentId);
  const ref = parent ? sourceRefOf(parent) : null;
  const pour = ref ? pours[ref] : undefined;
  if (parent && pour?.status === 'ready') {
    const masked = new Set(parent.hidden);
    for (const id of pour.ids) if (!masked.has(id)) ids.add(id);
  }
  return ids;
}

/** Les articles masqués d'une source qu'elle déverse ENCORE — le « −N » ; `null` tant que son contenu n'est pas lu. */
export function hiddenCount(tile: StudioTile, pours: Record<string, PourState>): number | null {
  const ref = sourceRefOf(tile);
  const pour = ref ? pours[ref] : undefined;
  if (!pour || pour.status !== 'ready') return null;
  const poured = new Set(pour.ids);
  return tile.hidden.filter((a) => poured.has(a)).length;
}

/** MASQUE DES ARTICLES DÉVERSÉS — ils restent au catalogue, et la tuile les rétablit d'un geste. */
export function hidePoured(layout: StudioLayout, sourceTileId: string, articleIds: string[]): StudioLayout {
  return {
    ...layout,
    tiles: layout.tiles.map((tile) => (tile.id === sourceTileId ? { ...tile, hidden: [...new Set([...tile.hidden, ...articleIds])] } : tile)),
  };
}

/** RÉTABLIT tout ce qu'une source a masqué. */
export function restoreHidden(layout: StudioLayout, sourceTileId: string): StudioLayout {
  return { ...layout, tiles: layout.tiles.map((tile) => (tile.id === sourceTileId ? { ...tile, hidden: [] } : tile)) };
}

/**
 * L'ORDRE RETOUCHÉ QUE DONNENT CES CELLULES — et celui des articles qu'elles ne montrent pas (au-delà de la borne),
 * gardé à la suite : déplacer une tuile visible ne doit pas défaire un rangement qu'on ne voit pas.
 */
function orderFrom(cellKeys: readonly string[], previous: readonly string[], swap?: { from: string; to: string }): string[] {
  const shown = cellKeys.map((k) => {
    const key = swap && k === swap.from ? swap.to : k;
    return isPouredKey(key) ? articleKey(pouredArticle(key)) : tileKey(key);
  });
  // ⚠️ La clé d'un article devenu tuile ne survit pas en fin d'ordre : elle y attendrait son retour sous une AUTRE place
  //    que la sienne, et le jour où la tuile repart, sa clé d'article ferait doublon (refusé par le serveur).
  const seen = new Set(swap && isPouredKey(swap.from) ? [...shown, articleKey(pouredArticle(swap.from))] : shown);
  return [...shown, ...previous.filter((k) => !seen.has(k))];
}

/** RÉORDONNE LE NIVEAU D'UNE SOURCE — l'ordre des cellules devient l'ordre retouché ; ses tuiles posées suivent. */
export function reorderSource(layout: StudioLayout, sourceTileId: string, cellKeys: readonly string[]): StudioLayout {
  const source = tileById(layout, sourceTileId);
  if (!source) return layout;
  const relaid = reorderLevel(layout, sourceTileId, cellKeys.filter((k) => !isPouredKey(k)));
  const order = orderFrom(cellKeys, source.order);
  return { ...relaid, tiles: relaid.tiles.map((tile) => (tile.id === sourceTileId ? { ...tile, order } : tile)) };
}

/** Déplace une cellule d'un cran dans le niveau d'une source — le geste des flèches. */
export function nudgeInSource(layout: StudioLayout, sourceTileId: string, cellKeys: readonly string[], key: string, delta: number): StudioLayout {
  const keys = [...cellKeys];
  const from = keys.indexOf(key);
  const to = from + delta;
  if (from < 0 || to < 0 || to >= keys.length) return layout;
  keys.splice(from, 1);
  keys.splice(to, 0, key);
  return reorderSource(layout, sourceTileId, keys);
}

/**
 * RETOUCHER UN ARTICLE DÉVERSÉ EN FAIT UNE TUILE À PART ENTIÈRE, À SA PLACE (maquette « matérialisation ») — il cesse
 * de suivre la source. ⚠️ Pas de marque « matérialisé » à tenir : un article POSÉ dans le niveau n'est pas déversé une
 * seconde fois (règle du contrat) ; retirer cette tuile le MASQUE à sa place (`releaseFromSource`), rétablissable.
 */
export function materialize(
  layout: StudioLayout, sourceTileId: string, cellKeys: readonly string[], articleId: string,
  patch: TilePatch,
): { layout: StudioLayout; id: string } {
  const source = tileById(layout, sourceTileId);
  if (!source) return { layout, id: '' };
  const id = newTileId();
  const tile: StudioTile = {
    id, parentId: sourceTileId, kind: 'article', articleId, label: patch.label ?? null, w: patch.w ?? 1, h: patch.h ?? 1,
    sourceId: null, hidden: [], order: [], ...NO_LOOK,
    ...(patch.color !== undefined ? { color: patch.color } : {}), ...(patch.visual !== undefined ? { visual: patch.visual } : {}),
    ...(patch.icon !== undefined ? { icon: patch.icon } : {}), ...(patch.photo !== undefined ? { photo: patch.photo } : {}),
    ...(patch.photoSize !== undefined ? { photoSize: patch.photoSize } : {}),
  };
  const order = orderFrom(cellKeys, source.order, { from: pouredKey(articleId), to: id });
  return {
    layout: { ...layout, tiles: [...layout.tiles.map((x) => (x.id === sourceTileId ? { ...x, order } : x)), tile] },
    id,
  };
}

/** Un résultat de « Où est cet article ? » : la cellule à montrer, et le niveau à ouvrir pour la voir. */
export interface FindHit { key: string; name: string; code: string | null; kind: string; level: string | null }

/**
 * « OÙ EST CET ARTICLE ? » — parcourt tout l'arbre, chaque résultat connaît son niveau. Sur cent tuiles réparties en
 * étages, c'est le seul moyen de retrouver un article sans tout rouvrir (maquette `findAll`). Les articles DÉVERSÉS
 * sont cherchés dans les sources dont le contenu est lu ; l'écran fait lire les autres dès que la recherche s'ouvre.
 */
export function findTiles(
  layout: StudioLayout, query: string, articles: Record<string, StudioArticle>, sources: Record<string, StudioSource>,
  pours: Record<string, PourState>, limit = 40,
): FindHit[] {
  const q = query.trim().toLowerCase();
  if (q.length < 2) return [];
  const out: FindHit[] = [];
  const matches = (name: string, a: StudioArticle | undefined): boolean =>
    [name, a?.gencode ?? '', a?.family ?? ''].join(' ').toLowerCase().includes(q);
  const walk = (parent: string | null): void => {
    for (const key of levelCells(layout, parent, pours).keys) {
      if (out.length >= limit) return;
      if (isPouredKey(key)) {
        const a = articles[pouredArticle(key)];
        if (a && matches(a.name, a)) out.push({ key, name: a.name, code: a.gencode, kind: t('Article'), level: parent });
        continue;
      }
      const tile = tileById(layout, key);
      if (!tile) continue;
      const a = tile.articleId ? articles[tile.articleId] : undefined;
      const name = tileName(tile, articles, sources);
      if (tile.kind !== 'empty' && matches(name, a)) out.push({ key, name, code: a?.gencode ?? null, kind: kindLabel(tile.kind), level: parent });
      if (opensLevel(tile.kind)) walk(tile.id);
    }
  };
  walk(null);
  return out;
}

/**
 * COCHER UNE CAISSE LA RETIRE DE TOUTE AUTRE DISPOSITION — une caisse n'affiche qu'un écran (maquette
 * `toggleCaisse`). Le geste dit d'où elle vient, pour que l'écran le dise aussi plutôt que de le faire en silence.
 */
export function toggleRegister(
  doc: StudioDocument, layoutId: string, register: string,
): { doc: StudioDocument; removed: boolean; from: string | null } {
  const current = doc.layouts.find((l) => l.id === layoutId);
  if (!current) return { doc, removed: false, from: null };
  const had = current.registers.includes(register);
  const from = had ? null : doc.layouts.find((l) => l.id !== layoutId && l.registers.includes(register))?.id ?? null;
  const layouts = doc.layouts.map((l) => {
    if (l.id === layoutId) {
      return { ...l, registers: had ? l.registers.filter((r) => r !== register) : [...l.registers, register].sort() };
    }
    return l.id === from ? { ...l, registers: l.registers.filter((r) => r !== register) } : l;
  });
  return { doc: { ...doc, layouts }, removed: had, from };
}

/**
 * UN GROUPE DE CAISSES COCHÉ D'UN GESTE — toutes ses caisses viennent ici (et quittent leur disposition), ou toutes
 * repartent sur la disposition par défaut (maquette `assignCaisses`). Même règle que `toggleRegister`, en une écriture.
 * - `on` : `true` les affecte toutes à cette disposition ; `false` les en retire toutes.
 */
export function assignRegisters(doc: StudioDocument, layoutId: string, registers: readonly string[], on: boolean): StudioDocument {
  if (!doc.layouts.some((l) => l.id === layoutId)) return doc;
  const set = new Set(registers);
  const layouts = doc.layouts.map((l) => {
    const rest = l.registers.filter((r) => !set.has(r));
    if (l.id !== layoutId) return on ? { ...l, registers: rest } : l;
    return { ...l, registers: on ? [...rest, ...set].sort() : rest };
  });
  return { ...doc, layouts };
}

export function setSettings(doc: StudioDocument, layoutId: string, patch: Partial<StudioSettings>): StudioDocument {
  return withLayout(doc, layoutId, (l) => ({ ...l, settings: { ...l.settings, ...patch } }));
}

/** Les caisses qui ne sont rattachées à aucune disposition — elles affichent la disposition par défaut. */
export function freeRegisters(doc: StudioDocument, registers: readonly string[]): string[] {
  const taken = new Set(doc.layouts.flatMap((l) => l.registers));
  return registers.filter((r) => !taken.has(r));
}

/** Le niveau que l'on compose, rail compris : rail actif, l'accueil n'est plus un écran, c'est le rail. */
export function railButtons(layout: StudioLayout): StudioTile[] {
  return levelTiles(layout, null).filter((tile) => tile.kind !== 'empty');
}

/**
 * LE CHEMIN EFFECTIF DU COMPOSEUR — rail actif, la grille ouvre d'office la première tuile du rail qui ouvre un niveau
 * (dossier ou source — maquette `ep`).
 */
export function effectivePath(layout: StudioLayout, path: string[]): string[] {
  if (!layout.settings.rail || path.length > 0) return path;
  const first = railButtons(layout).find((tile) => opensLevel(tile.kind));
  return first ? [first.id] : path;
}

/** Deux documents décrivent-ils le même état ? L'ordre ENTRE niveaux ne compte pas, seul l'ordre DANS un niveau. */
export function sameDocument(a: StudioDocument, b: StudioDocument): boolean {
  const canon = (doc: StudioDocument): string => JSON.stringify({
    d: doc.defaultLayoutId,
    l: [...doc.layouts].sort((x, y) => x.id.localeCompare(y.id)).map((l) => {
      const parents = [...new Set(l.tiles.map((tile) => tile.parentId ?? ''))].sort();
      return {
        // ⚠️ TOUS LES RÉGLAGES : un réglage oublié ici ferait passer son changement pour « rien de changé », jamais enregistré.
        id: l.id, s: [l.settings.cols, l.settings.rail, l.settings.home, l.settings.visual, l.settings.articlesVisual, l.settings.newVisual],
        r: [...l.registers].sort(),
        // ⚠️ Les masqués forment un ENSEMBLE, l'ordre retouché une SUITE : comme le serveur, on range le premier seulement.
        t: parents.flatMap((p) => l.tiles.filter((tile) => (tile.parentId ?? '') === p)
          .map((tile) => [
            tile.id, tile.parentId, tile.kind, tile.articleId, tile.label, tile.w, tile.h, tile.sourceId, [...tile.hidden].sort(), tile.order,
            tile.color, tile.visual, tile.icon, tile.photo && [tile.photo.url, tile.photo.thumbUrl, tile.photo.x, tile.photo.y, tile.photo.zoom],
            tile.photoSize, [tile.pour.color, tile.pour.visual, tile.pour.icon, tile.pour.photoSize],
          ])),
      };
    }),
  });
  return canon(a) === canon(b);
}
